import { useContext, useEffect, useRef, useState } from 'react'
import { ThemeContext } from '../../providers/ThemeProvider'
import { tokenize, type CodeHighlightToken } from '@/lib/shikiHighlighter'
import { CODE_STREAM_MAX_LINES } from './codeStreamTail'

/**
 * The code being written, live, under the running "Generating Trading Code" card.
 *
 * Purely visual, and purely presentational: CodeGenerationLive owns the SSE
 * subscription and hands the tail of the code down as `lines`. This colours
 * them with the same Shiki tokenizer the Backtest tab's Code panel uses (a size
 * down, and without that panel's line numbers) and shows them bottom-anchored
 * so new lines appear to scroll in. Nothing is stored anywhere: the card mounts
 * the live area only while it is running, so done, failure and Stop all take
 * this down with it.
 */
interface CodeStreamSnippetProps {
  /** The last few lines of the code so far (see appendCodeTail). Empty renders nothing. */
  lines: string[]
}

export default function CodeStreamSnippet({ lines }: CodeStreamSnippetProps) {
  const themeCtx = useContext(ThemeContext) as { theme?: string } | undefined
  const isDark = themeCtx?.theme === 'dark'

  const [tokens, setTokens] = useState<CodeHighlightToken[][] | null>(null)

  // Same tokenizer and theme choice as BotBacktestCode. The tail is at most a
  // few hundred characters, so no debounce; the flag drops a stale result.
  const text = lines.join('\n')
  useEffect(() => {
    if (!text) return
    let cancelled = false
    tokenize(text, isDark ? 'github-dark' : 'github-light')
      .then((t) => {
        if (!cancelled) setTokens(t)
      })
      .catch(() => {
        if (!cancelled) setTokens(null)
      })
    return () => {
      cancelled = true
    }
  }, [text, isDark])

  // The chat's autoscroll keys off new messages, not a card growing beneath the
  // last one, so bring the box into view once when it appears.
  const visible = lines.length > 0
  const boxRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!visible) return
    boxRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
  }, [visible])

  if (!visible) return null

  // Render from the tokens, which carry their own text: colouring the raw lines
  // after the fact would flash uncoloured on every delta. The display trails
  // the buffer by one tokenize round-trip, a few milliseconds. Raw text is the
  // fallback until the first tokens arrive or if the tokenizer fails.
  const shown: CodeHighlightToken[][] = tokens ?? lines.map((line) => [{ content: line }])
  const pad = Math.max(0, CODE_STREAM_MAX_LINES - shown.length)

  return (
    <div className="pl-5 pt-1.5">
      <div
        ref={boxRef}
        aria-hidden="true"
        className="relative overflow-hidden rounded-md border border-border bg-card select-none pointer-events-none"
      >
        <div className="text-mono-sm text-foreground px-4 py-1.5 whitespace-pre">
          {/* Blank rows first: a fixed height without an arbitrary pixel value,
              and the code sits at the bottom so new lines read as scrolling in. */}
          {Array.from({ length: pad }, (_, i) => (
            <div key={`pad-${i}`}>{' '}</div>
          ))}
          {shown.map((row, i) => {
            const isLast = i === shown.length - 1
            const empty = row.every((t) => t.content === '')
            return (
              <div key={i}>
                {/* An empty div collapses to nothing; hold the line box open. */}
                {empty ? ' ' : null}
                {row.map((t, j) => (
                  // Shiki's token colour is content-driven syntax colour, not
                  // theming — the same exception CodeViewer carries.
                  <span key={j} style={t.color ? { color: t.color } : undefined}>
                    {t.content}
                  </span>
                ))}
                {isLast && <Caret />}
              </div>
            )
          })}
        </div>
        {/* The oldest line fades as it scrolls out. */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-4 bg-gradient-to-b from-card to-transparent" />
      </div>
    </div>
  )
}

/** A thin pulsing bar — the row above already has the orb. */
function Caret() {
  return (
    <span
      aria-hidden="true"
      className="inline-block w-0.5 h-3 ml-px align-middle bg-primary animate-pulse"
    />
  )
}
